import { useEffect, useState } from 'react';
import { SlidersHorizontal, X, Leaf } from 'lucide-react';
import { getSidebarFilters } from '../services/api.shop';
import { ProductCondition, SidebarFilterDto, SidebarFilterType } from '../types';
import { useTranslation } from '../context/TranslationContext';
import ShopCheckbox from './ShopCheckbox';

export interface ProductFilterState {
    minPrice: string;
    maxPrice: string;
    conditions: ProductCondition[];
    specFilters: string[];
    /** Multi-select developmental phases (MyRebaby / clinical bands). */
    growthPhaseIds: number[];
    /**
     * Free dual-handle age window in months (product range overlap).
     * null = not active (full slider = no free-range filter).
     * Mutually exclusive with growthPhaseIds in the UI.
     */
    ageMinMonths: number | null;
    ageMaxMonths: number | null;
}

export const emptyProductFilters = (): ProductFilterState => ({
    minPrice: '',
    maxPrice: '',
    conditions: [],
    specFilters: [],
    growthPhaseIds: [],
    ageMinMonths: null,
    ageMaxMonths: null,
});

const DEFAULT_AGE_SLIDER_MAX = 60;

/** Dual price slider: 0–100€ uses the left 55% so sub-€100 is usable (admin RangeStep=50 / 0–5000 was not). */
const PRICE_SLIDER_MAX = 100;
const LOW_PRICE_EUR = 100;
const LOW_PRICE_FRAC = 0.55;

function priceToSliderPos(price: number, domainMin: number, domainMax: number): number {
    const p = Math.min(domainMax, Math.max(domainMin, price));
    if (domainMax <= domainMin) return 0;
    const lowCap = Math.min(LOW_PRICE_EUR, domainMax);
    if (domainMax <= LOW_PRICE_EUR || domainMin >= LOW_PRICE_EUR) {
        return ((p - domainMin) / (domainMax - domainMin)) * PRICE_SLIDER_MAX;
    }
    if (p <= lowCap) {
        const span = lowCap - domainMin;
        return span <= 0 ? 0 : ((p - domainMin) / span) * LOW_PRICE_FRAC * PRICE_SLIDER_MAX;
    }
    const highSpan = domainMax - lowCap;
    const t = highSpan <= 0 ? 1 : (p - lowCap) / highSpan;
    return (LOW_PRICE_FRAC + t * (1 - LOW_PRICE_FRAC)) * PRICE_SLIDER_MAX;
}

function sliderPosToPrice(pos: number, domainMin: number, domainMax: number): number {
    const t = Math.min(PRICE_SLIDER_MAX, Math.max(0, pos)) / PRICE_SLIDER_MAX;
    if (domainMax <= domainMin) return domainMin;
    const lowCap = Math.min(LOW_PRICE_EUR, domainMax);
    if (domainMax <= LOW_PRICE_EUR || domainMin >= LOW_PRICE_EUR) {
        return domainMin + t * (domainMax - domainMin);
    }
    if (t <= LOW_PRICE_FRAC) {
        return domainMin + (t / LOW_PRICE_FRAC) * (lowCap - domainMin);
    }
    return lowCap + ((t - LOW_PRICE_FRAC) / (1 - LOW_PRICE_FRAC)) * (domainMax - lowCap);
}

function parsePriceBound(raw: string, fallback: number): number {
    if (raw === '') return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
}

const priceInputClass =
    'min-h-11 w-full rounded-md border border-[color-mix(in_srgb,var(--accent-color),transparent_55%)] bg-[var(--bg-color)] px-2.5 text-sm text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]';

function FilterPanel({
    filters,
    onFiltersChange,
    onClose,
    showClose,
    /** When true, Clear + eco sit outside (sticky mobile sheet footer). */
    hideFooter,
}: {
    filters: ProductFilterState;
    onFiltersChange: (filters: ProductFilterState) => void;
    onClose?: () => void;
    showClose?: boolean;
    hideFooter?: boolean;
}) {
    const { t } = useTranslation();
    const [sidebarFilters, setSidebarFilters] = useState<SidebarFilterDto[]>([]);

    useEffect(() => {
        getSidebarFilters()
            .then(setSidebarFilters)
            .catch(() => console.error('Failed to load sidebar filters'));
    }, []);

    const tr = (key: string | undefined, fallback: string) => {
        if (!key) return fallback;
        const val = t(key);
        return val !== key ? val : fallback;
    };

    const toggleCondition = (condition: ProductCondition) => {
        const next = filters.conditions.includes(condition)
            ? filters.conditions.filter((c) => c !== condition)
            : [...filters.conditions, condition];
        onFiltersChange({ ...filters, conditions: next });
    };

    const toggleSpecFilter = (specName: string, value: string) => {
        const key = `${specName}:${value}`;
        const next = filters.specFilters.includes(key)
            ? filters.specFilters.filter((f) => f !== key)
            : [...filters.specFilters, key];
        onFiltersChange({ ...filters, specFilters: next });
    };

    const toggleGrowthPhase = (phaseId: number) => {
        const next = filters.growthPhaseIds.includes(phaseId)
            ? filters.growthPhaseIds.filter((id) => id !== phaseId)
            : [...filters.growthPhaseIds, phaseId];
        onFiltersChange({
            ...filters,
            growthPhaseIds: next,
            // Phase checkboxes are developmental stages — clear free age window
            ageMinMonths: null,
            ageMaxMonths: null,
        });
    };

    const setAgeWindow = (minMonths: number, maxMonths: number, absoluteMin: number, absoluteMax: number) => {
        let lo = Math.min(minMonths, maxMonths);
        let hi = Math.max(minMonths, maxMonths);
        lo = Math.max(absoluteMin, Math.min(absoluteMax, lo));
        hi = Math.max(absoluteMin, Math.min(absoluteMax, hi));
        // Full span = no free-range filter
        if (lo <= absoluteMin && hi >= absoluteMax) {
            onFiltersChange({
                ...filters,
                ageMinMonths: null,
                ageMaxMonths: null,
            });
            return;
        }
        onFiltersChange({
            ...filters,
            ageMinMonths: lo,
            ageMaxMonths: hi,
            growthPhaseIds: [],
        });
    };

    const clearFilters = () => onFiltersChange(emptyProductFilters());

    const hasActiveFilters =
        filters.minPrice !== '' ||
        filters.maxPrice !== '' ||
        filters.conditions.length > 0 ||
        filters.specFilters.length > 0 ||
        filters.growthPhaseIds.length > 0 ||
        (filters.ageMinMonths != null && filters.ageMaxMonths != null);

    const renderFilterSection = (filter: SidebarFilterDto) => {
        const title = tr(filter.titleTranslationKey, filter.specificationName ?? filter.typeName);

        if (filter.type === SidebarFilterType.Price) {
            const domainMin = filter.rangeMin ?? 0;
            const domainMax = filter.rangeMax ?? 5000;
            const minVal = parsePriceBound(filters.minPrice, domainMin);
            const maxVal = parsePriceBound(filters.maxPrice, domainMax);
            const minPos = priceToSliderPos(minVal, domainMin, domainMax);
            const maxPos = priceToSliderPos(maxVal, domainMin, domainMax);

            const commitMin = (next: number) => {
                const cap = filters.maxPrice === '' ? domainMax : maxVal;
                const clamped = Math.min(Math.max(domainMin, next), cap);
                onFiltersChange({
                    ...filters,
                    minPrice: clamped <= domainMin ? '' : String(clamped),
                });
            };
            const commitMax = (next: number) => {
                const floor = filters.minPrice === '' ? domainMin : minVal;
                const clamped = Math.max(Math.min(domainMax, next), floor);
                onFiltersChange({
                    ...filters,
                    maxPrice: clamped >= domainMax ? '' : String(clamped),
                });
            };

            return (
                <section key={filter.id}>
                    <h3 className="mb-3 text-sm font-medium text-[var(--text-color)]">{title}</h3>
                    <div className="space-y-3">
                        <div className="relative h-8 px-0.5">
                            <div className="pointer-events-none absolute left-0 right-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-[color-mix(in_srgb,var(--text-color),transparent_85%)]" />
                            <div
                                className="pointer-events-none absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-[var(--accent-color)]"
                                style={{
                                    left: `${minPos}%`,
                                    right: `${100 - maxPos}%`,
                                }}
                            />
                            <input
                                type="range"
                                min={0}
                                max={PRICE_SLIDER_MAX}
                                step={0.5}
                                value={minPos}
                                aria-label={tr('filter.price.min', 'Min')}
                                onChange={(e) =>
                                    commitMin(
                                        Math.round(
                                            sliderPosToPrice(Number(e.target.value), domainMin, domainMax)
                                        )
                                    )
                                }
                                className="age-range-thumb absolute left-0 top-0 z-[2] h-8 w-full appearance-none bg-transparent"
                                style={{ zIndex: minPos > PRICE_SLIDER_MAX - maxPos ? 4 : 2 }}
                            />
                            <input
                                type="range"
                                min={0}
                                max={PRICE_SLIDER_MAX}
                                step={0.5}
                                value={maxPos}
                                aria-label={tr('filter.price.max', 'Max')}
                                onChange={(e) =>
                                    commitMax(
                                        Math.round(
                                            sliderPosToPrice(Number(e.target.value), domainMin, domainMax)
                                        )
                                    )
                                }
                                className="age-range-thumb absolute left-0 top-0 z-[3] h-8 w-full appearance-none bg-transparent"
                            />
                        </div>
                        <div className="flex justify-between text-[10px] text-[color-mix(in_srgb,var(--text-color),transparent_45%)]">
                            <span>€{domainMin}</span>
                            <span>€{domainMax}+</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <label className="flex min-w-0 flex-col gap-1">
                                <span className="text-xs text-[color-mix(in_srgb,var(--text-color),transparent_30%)]">
                                    {tr('filter.price.min', 'Min')}
                                </span>
                                <input
                                    type="number"
                                    inputMode="decimal"
                                    min={domainMin}
                                    max={domainMax}
                                    step={1}
                                    placeholder={String(domainMin)}
                                    value={filters.minPrice}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        if (raw === '') {
                                            onFiltersChange({ ...filters, minPrice: '' });
                                            return;
                                        }
                                        const n = Number(raw);
                                        if (!Number.isFinite(n)) return;
                                        commitMin(n);
                                    }}
                                    className={priceInputClass}
                                />
                            </label>
                            <label className="flex min-w-0 flex-col gap-1">
                                <span className="text-xs text-[color-mix(in_srgb,var(--text-color),transparent_30%)]">
                                    {tr('filter.price.max', 'Max')}
                                </span>
                                <input
                                    type="number"
                                    inputMode="decimal"
                                    min={domainMin}
                                    max={domainMax}
                                    step={1}
                                    placeholder={`${domainMax}+`}
                                    value={filters.maxPrice}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        if (raw === '') {
                                            onFiltersChange({ ...filters, maxPrice: '' });
                                            return;
                                        }
                                        const n = Number(raw);
                                        if (!Number.isFinite(n)) return;
                                        commitMax(n);
                                    }}
                                    className={priceInputClass}
                                />
                            </label>
                        </div>
                    </div>
                </section>
            );
        }

        if (filter.type === SidebarFilterType.Condition) {
            return (
                <section key={filter.id}>
                    <h3 className="mb-3 text-sm font-medium text-[var(--text-color)]">{title}</h3>
                    <div className="flex flex-col gap-2">
                        {filter.options.map((option) => {
                            const condition = option.value as ProductCondition;
                            return (
                                <label key={option.id} className="flex cursor-pointer items-center gap-2 text-sm text-[var(--text-color)]">
                                    <ShopCheckbox
                                        checked={filters.conditions.includes(condition)}
                                        onChange={() => toggleCondition(condition)}
                                    />
                                    <span className="flex items-center gap-1.5">
                                        {option.icon && <span>{option.icon}</span>}
                                        {tr(option.labelTranslationKey, option.value)}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                </section>
            );
        }

        if (filter.type === SidebarFilterType.GrowthPhase) {
            const absoluteMin = 0;
            const absoluteMax = Math.max(
                DEFAULT_AGE_SLIDER_MAX,
                ...filter.options.map((o) => o.maxAgeMonths ?? 0).filter((n) => n > 0)
            );
            const sliderMin = filters.ageMinMonths ?? absoluteMin;
            const sliderMax = filters.ageMaxMonths ?? absoluteMax;
            const freeRangeActive = filters.ageMinMonths != null && filters.ageMaxMonths != null;

            return (
                <section key={filter.id}>
                    <h3 className="mb-1 text-sm font-medium text-[var(--text-color)]">
                        {tr(filter.titleTranslationKey, tr('filter.growthPhase', 'Development phase'))}
                    </h3>
                    <p className="mb-3 text-xs leading-snug text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                        {tr(
                            'filter.growthPhase.hint',
                            'Use the slider to enter the age range of the product youre looking for.'
                        )}
                    </p>

                    {/* Dual-handle month window — free custom range */}
                    <div className="mb-4">
                        <div className="mb-2 flex items-center justify-between text-xs text-[color-mix(in_srgb,var(--text-color),transparent_30%)]">
                            <span>{tr('filter.growthPhase.ageRange', 'Age range (months)')}</span>
                            <span className="font-medium text-[var(--text-color)]">
                                {sliderMin}–{sliderMax}
                                {sliderMax >= absoluteMax && !freeRangeActive ? '+' : ''}
                            </span>
                        </div>
                        <div className="relative h-8 px-0.5">
                            {/* Track background */}
                            <div className="pointer-events-none absolute left-0 right-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-[color-mix(in_srgb,var(--text-color),transparent_85%)]" />
                            {/* Active segment */}
                            <div
                                className="pointer-events-none absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-[var(--accent-color)]"
                                style={{
                                    left: `${(sliderMin / absoluteMax) * 100}%`,
                                    right: `${100 - (sliderMax / absoluteMax) * 100}%`,
                                }}
                            />
                            <input
                                type="range"
                                min={absoluteMin}
                                max={absoluteMax}
                                step={1}
                                value={sliderMin}
                                aria-label={tr('filter.growthPhase.minMonths', 'Minimum age (months)')}
                                onChange={(e) => {
                                    const nextMin = Number(e.target.value);
                                    setAgeWindow(nextMin, Math.max(nextMin, sliderMax), absoluteMin, absoluteMax);
                                }}
                                className="age-range-thumb absolute left-0 top-0 z-[2] h-8 w-full appearance-none bg-transparent"
                                style={{ zIndex: sliderMin > absoluteMax - sliderMin ? 4 : 2 }}
                            />
                            <input
                                type="range"
                                min={absoluteMin}
                                max={absoluteMax}
                                step={1}
                                value={sliderMax}
                                aria-label={tr('filter.growthPhase.maxMonths', 'Maximum age (months)')}
                                onChange={(e) => {
                                    const nextMax = Number(e.target.value);
                                    setAgeWindow(Math.min(sliderMin, nextMax), nextMax, absoluteMin, absoluteMax);
                                }}
                                className="age-range-thumb absolute left-0 top-0 z-[3] h-8 w-full appearance-none bg-transparent"
                            />
                        </div>
                        <div className="mt-1 flex justify-between text-[10px] text-[color-mix(in_srgb,var(--text-color),transparent_45%)]">
                            <span>{absoluteMin}</span>
                            <span>{absoluteMax}+</span>
                        </div>
                    </div>

                    <p className="mb-2 text-xs font-medium text-[var(--text-color)]">
                        {tr('filter.growthPhase.stages', 'Or pick a developmental stage')}
                    </p>
                    <div className="flex flex-col gap-2">
                        {filter.options.map((option) => {
                            const phaseId = option.checklistPhaseId ?? Number(option.value);
                            return (
                                <label key={option.id} className="flex cursor-pointer items-center gap-2 text-sm text-[var(--text-color)]">
                                    <ShopCheckbox
                                        checked={filters.growthPhaseIds.includes(phaseId)}
                                        onChange={() => toggleGrowthPhase(phaseId)}
                                    />
                                    <span className="flex items-center gap-1.5">
                                        {option.icon && <span>{option.icon}</span>}
                                        {tr(option.labelTranslationKey, option.value)}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                </section>
            );
        }

        if (filter.type === SidebarFilterType.Specification && filter.specificationName) {
            const specName = filter.specificationName;
            return (
                <section key={filter.id}>
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-medium text-[var(--text-color)]">
                        {title}
                    </h3>
                    <div className="flex flex-col gap-2">
                        {filter.options.map((option) => {
                            const key = `${specName}:${option.value}`;
                            return (
                                <label key={option.id} className="flex cursor-pointer items-center gap-2 text-sm text-[var(--text-color)]">
                                    <ShopCheckbox
                                        checked={filters.specFilters.includes(key)}
                                        onChange={() => toggleSpecFilter(specName, option.value)}
                                    />
                                    <span className="flex items-center gap-1.5">
                                        {option.icon && <span>{option.icon}</span>}
                                        {tr(option.labelTranslationKey, option.value)}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                </section>
            );
        }

        return null;
    };

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-[var(--text-color)]">
                    {tr('filter.title', 'Filters')}
                </h2>
                {showClose && onClose && (
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-full p-1 hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_70%)]"
                        aria-label={tr('common.close', 'Close')}
                    >
                        <X className="h-5 w-5" />
                    </button>
                )}
            </div>

            {sidebarFilters.map(renderFilterSection)}

            {!hideFooter && (
                <FilterPanelFooter
                    hasActiveFilters={hasActiveFilters}
                    onClear={clearFilters}
                    tr={tr}
                />
            )}
        </div>
    );
}

function FilterPanelFooter({
    hasActiveFilters,
    onClear,
    tr,
}: {
    hasActiveFilters: boolean;
    onClear: () => void;
    tr: (key: string, fallback: string) => string;
}) {
    return (
        <div className="flex flex-col gap-3">
            {hasActiveFilters && (
                <button
                    type="button"
                    onClick={onClear}
                    className="text-sm font-medium text-[var(--accent-color)] underline hover:opacity-80"
                >
                    {tr('filter.clear', 'Clear all filters')}
                </button>
            )}

            <div className="rounded-xl bg-[var(--accent-color)]/25 p-4">
                <div className="flex items-start gap-2">
                    <Leaf className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-color)]" />
                    <div>
                        <p className="text-sm font-semibold text-[var(--text-color)]">
                            {tr('filter.eco.title', 'Eco-Friendly')}
                        </p>
                        <p className="mt-1 text-xs text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">
                            {tr(
                                'filter.eco.text',
                                'Every purchase helps reduce waste and supports sustainable parenting.'
                            )}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function countActiveFilters(filters: ProductFilterState): number {
    let n = 0;
    if (filters.minPrice !== '') n += 1;
    if (filters.maxPrice !== '') n += 1;
    n += filters.conditions.length;
    n += filters.specFilters.length;
    n += filters.growthPhaseIds.length;
    if (filters.ageMinMonths != null && filters.ageMaxMonths != null) n += 1;
    return n;
}

/** Compact chip for the category row (mobile). Desktop sidebar is separate. */
export function MobileFilterChip({
    activeCount,
    onClick,
    isOpen,
}: {
    activeCount: number;
    onClick: () => void;
    isOpen?: boolean;
}) {
    const { t } = useTranslation();
    const tr = (key: string, fallback: string) => {
        const val = t(key);
        return val !== key ? val : fallback;
    };
    const hasActive = activeCount > 0;

    return (
        <button
            type="button"
            onClick={onClick}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-2 min-h-11 whitespace-nowrap text-sm font-medium transition-all lg:hidden ${
                hasActive
                    ? 'border-[var(--accent-color)] bg-[var(--accent-color)] text-white shadow-sm'
                    : 'border-[var(--accent-color)]/50 bg-[color-mix(in_srgb,var(--bg-color),white_40%)] text-[var(--text-color)] hover:bg-[var(--accent-color)]/30'
            }`}
        >
            <SlidersHorizontal className="h-4 w-4 shrink-0" aria-hidden />
            <span>{tr('filter.chip', 'Filter')}</span>
            {hasActive && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--text-color)] px-1.5 text-[10px] font-bold text-[var(--bg-color)]">
                    {activeCount}
                </span>
            )}
        </button>
    );
}

/**
 * Option C trial: floating filter control over the product list.
 * Remove this component + Home flag if you keep the category-row chip instead.
 */
export function MobileFilterFab({
    activeCount,
    onClick,
    isOpen,
}: {
    activeCount: number;
    onClick: () => void;
    isOpen?: boolean;
}) {
    const { t } = useTranslation();
    const tr = (key: string, fallback: string) => {
        const val = t(key);
        return val !== key ? val : fallback;
    };
    const hasActive = activeCount > 0;

    // Upper-left under sticky header + category row (not viewport-centered — it covered listings).
    return (
        <button
            type="button"
            onClick={onClick}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            aria-label={tr('filter.chip', 'Filter')}
            className={`fixed left-3 z-30 flex items-center gap-2 rounded-full border-2 px-4 py-3 shadow-lg transition active:opacity-90 lg:hidden ${
                hasActive
                    ? 'border-[var(--accent-color)] bg-[var(--accent-color)] text-white'
                    : 'border-[var(--accent-color)] bg-[var(--bg-color)] text-[var(--text-color)]'
            }`}
            style={{ top: 'calc(var(--shop-header-height, 4rem) + 4.75rem)' }}
        >
            <SlidersHorizontal className="h-5 w-5 shrink-0" aria-hidden />
            <span className="text-sm font-semibold">{tr('filter.chip', 'Filter')}</span>
            {hasActive && (
                <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-[var(--text-color)] px-1.5 text-xs font-bold text-[var(--bg-color)]">
                    {activeCount}
                </span>
            )}
        </button>
    );
}

interface ProductFilterSidebarProps {
    filters: ProductFilterState;
    onFiltersChange: (filters: ProductFilterState) => void;
    /** Controlled mobile drawer (opened from category-row chip) */
    mobileOpen?: boolean;
    onMobileOpenChange?: (open: boolean) => void;
}

export default function ProductFilterSidebar({
    filters,
    onFiltersChange,
    mobileOpen: mobileOpenProp,
    onMobileOpenChange,
}: ProductFilterSidebarProps) {
    const { t } = useTranslation();
    const [internalOpen, setInternalOpen] = useState(false);

    const isControlled = mobileOpenProp !== undefined;
    const mobileOpen = isControlled ? mobileOpenProp : internalOpen;
    const setMobileOpen = (open: boolean) => {
        if (isControlled) onMobileOpenChange?.(open);
        else setInternalOpen(open);
    };

    const tr = (key: string, fallback: string) => {
        const val = t(key);
        return val !== key ? val : fallback;
    };

    useEffect(() => {
        if (!mobileOpen) return;
        document.body.classList.add('scroll-locked');
        return () => document.body.classList.remove('scroll-locked');
    }, [mobileOpen]);

    return (
        <>
            {/* Desktop only — mobile trigger lives in CategoryFilter chip row */}
            <aside className="hidden w-64 shrink-0 lg:block">
                <div
                    className="sticky rounded-2xl bg-[color-mix(in_srgb,var(--bg-color),white_15%)] p-5 shadow-md"
                    style={{ top: 'calc(var(--shop-header-height, 4rem) + 1rem)' }}
                >
                    <FilterPanel filters={filters} onFiltersChange={onFiltersChange} />
                </div>
            </aside>

            {mobileOpen && (
                <div
                    className="fixed inset-0 z-[60] lg:hidden"
                    role="dialog"
                    aria-modal="true"
                    aria-label={tr('filter.title', 'Filters')}
                >
                    <div
                        className="absolute inset-0 bg-black/40"
                        onClick={() => setMobileOpen(false)}
                        aria-hidden="true"
                    />
                    {/*
                      Mobile sheet ends above bottom nav (no crop of filter content under nav).
                      Internal scroll + sticky footer keeps Clear / eco always reachable.
                    */}
                    <div
                        className="absolute left-0 top-0 flex w-80 max-w-[85vw] flex-col bg-[var(--bg-color)] shadow-xl"
                        style={{
                            bottom:
                                'calc(var(--mobile-bottom-nav-height, 4rem) + env(safe-area-inset-bottom, 0px))',
                        }}
                    >
                        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-5 pb-3">
                            <FilterPanel
                                filters={filters}
                                onFiltersChange={onFiltersChange}
                                onClose={() => setMobileOpen(false)}
                                showClose
                                hideFooter
                            />
                        </div>
                        <div className="shrink-0 border-t border-[color-mix(in_srgb,var(--accent-color),transparent_70%)] bg-[var(--bg-color)] p-4">
                            <FilterPanelFooter
                                hasActiveFilters={
                                    filters.minPrice !== '' ||
                                    filters.maxPrice !== '' ||
                                    filters.conditions.length > 0 ||
                                    filters.specFilters.length > 0 ||
                                    filters.growthPhaseIds.length > 0 ||
                                    (filters.ageMinMonths != null && filters.ageMaxMonths != null)
                                }
                                onClear={() => onFiltersChange(emptyProductFilters())}
                                tr={tr}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}